import { ArrowUp, ArrowUpRight, Facebook, Linkedin, Mail, MapPin, Phone, Twitter } from 'lucide-react';
import { CONTACTS, EVENTS, SOCIALS, VENUE_IMG, assetUrl } from '../data';
import { Reveal, SectionHead, useApp, useSite } from './shared';

const SOCIAL_ICONS = { facebook: Facebook, twitter: Twitter, linkedin: Linkedin };

export default function Contact() {
  const { scrollToId } = useApp();
  const { settings } = useSite();

  return (
    <>
      <section id="contact" className="tex-lines border-t border-line/10 bg-ink-2/40 py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <SectionHead
            eyebrow="Contact us"
            title={
              <>
                Ready to step into an exclusive gathering of{' '}
                <span className="text-lime">top CXOs of India?</span>
              </>
            }
          />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {CONTACTS.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`contact-card-${i}`}
                  className="group h-full rounded-2xl border border-line/10 bg-ink-3/50 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-lime/50"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">{c.role}</p>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{c.name}</h3>
                  <div className="mt-6 space-y-3 text-sm">
                    <a
                      href={`tel:${c.phone.replace(/\s+/g, '')}`}
                      data-testid={`contact-phone-${i}`}
                      className="flex items-center gap-3 text-slate-300 transition-colors hover:text-lime"
                    >
                      <Phone size={15} className="text-lime" /> {c.phone}
                    </a>
                    <a
                      href={`mailto:${c.email}`}
                      data-testid={`contact-email-${i}`}
                      className="flex items-center gap-3 break-all text-slate-300 transition-colors hover:text-lime"
                    >
                      <Mail size={15} className="shrink-0 text-lime" /> {c.email}
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-14">
            <div
              data-testid="venue-card"
              className="grid grid-cols-1 overflow-hidden rounded-2xl border border-line/10 lg:grid-cols-2"
            >
              <div className="relative h-64 lg:h-auto">
                <img src={VENUE_IMG} alt="Proposed Venues" loading="lazy" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink/40" />
              </div>
              <div className="bg-ink-3/60 p-9 md:p-12">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">Proposed Venues</p>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Two Cities. <span className="text-lime">One Summit.</span>
                </h3>
                <div className="mt-6 space-y-7">
                  {EVENTS.map((ev, i) => (
                    <div key={ev.city} data-testid={`venue-city-${i}`} className="border-l-2 border-lime/50 pl-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">
                        {ev.city} · {ev.date}
                      </p>
                      <p className="mt-2 font-display text-lg font-bold tracking-tight">{ev.venue}</p>
                      <p className="mt-2 flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                        <MapPin size={15} className="mt-0.5 shrink-0 text-lime" />
                        {ev.address}
                      </p>
                      <a
                        href={ev.map}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`venue-map-link-${i}`}
                        className="group mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-lime transition-all hover:gap-3"
                      >
                        Map View <ArrowUpRight size={13} />
                      </a>
                    </div>
                  ))}
                </div>
                <p className="mt-8 font-mono text-[11px] uppercase leading-loose tracking-[0.25em] text-slate-400">
                  Event Timing: 08:00 AM to 10.00 PM
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="relative border-t border-line/10 py-16">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-10 md:flex-row md:items-center">
            <a href="https://cioaxis.com" target="_blank" rel="noopener noreferrer" className="group block" data-testid="footer-logo">
              <img
                src={assetUrl(settings.logo_footer)}
                alt="CIO AXIS"
                loading="lazy"
                className="logo-on-dark h-9 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03] md:h-10"
              />
            </a>
            <div className="flex items-center gap-3">
              {SOCIALS.map((s, i) => {
                const Icon = SOCIAL_ICONS[s.name];
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`footer-social-${i}`}
                    aria-label={s.name}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-line/10 text-slate-400 transition-all hover:border-lime/60 hover:text-lime"
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
              <button
                data-testid="footer-back-to-top"
                onClick={() => scrollToId('home')}
                className="ml-2 flex h-10 w-10 items-center justify-center rounded-full bg-lime text-ink transition-all hover:shadow-[0_0_28px_rgba(153,204,0,0.5)]"
                aria-label="go to top"
              >
                <ArrowUp size={16} />
              </button>
            </div>
          </div>
          <p className="mt-10 max-w-3xl text-[13px] leading-relaxed text-slate-500">
            CIO AXIS is a premier insights platform for today&apos;s IT leadership — including CIOs, CISOs, CTOs, CDOs,
            DPOs, Heads of IT, and other C-suite decision-makers. Our mission is to empower these leaders to make smarter,
            faster IT decisions across every industry.
          </p>
          <div className="mt-10 border-t border-line/5 pt-8">
            <p className="mx-auto max-w-4xl text-center text-[10.5px] leading-relaxed text-slate-500" data-testid="footer-legal">
              ©2026-27 CIOAXIS | CISO Connect. Innovative CIO Awards, InfoSec Maestros, Defenders 100, INSIGHTS CXO
              Awards, CISO &amp; Cyber Pioneers Awards, CXO100 VISION Awards, are brands of BitStream Mediaworks Pvt Ltd.
              All rights reserved. Other products and service names might be trademarks of other companies.
            </p>
            <div className="mt-8 flex justify-center">
              <a href="https://cioaxis.com" target="_blank" rel="noopener noreferrer" aria-label="CIO AXIS" data-testid="footer-bottom-logo">
                <img
                  src={assetUrl(settings.logo_bottom)}
                  alt="CIO AXIS"
                  loading="lazy"
                  className="logo-reverse h-6 w-auto object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
